import {
  ColumnFiltersState,
  ColumnSizingState,
  ExpandedState,
  getCoreRowModel,
  getExpandedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import React, { useEffect, useState } from 'react';
import {
  ColumnGroup,
  ColumnResizeConfig,
  PaginationConfigProps,
  RowSelectionConfig,
  SearchConfigProps,
  TableSortConfig,
  TreeNode,
} from '../types';
import { createColumnDef } from '../utils';
import { useColumnVisibility } from './useColumnVisibility';
import { usePaginationState } from './usePaginationState';
import { useSortingState } from './useSortingState';

export function useTable<TData, TValue>({
  data,
  paginationConfig,
  searchConfig,
  groups,
  rowSelectionConfig,
  sortingConfig,
  defaultVisibleColumns,
  columnResizeConfig,
  fitContentCapsRef,
}: {
  data: TreeNode<TData>[];
  paginationConfig: PaginationConfigProps;
  searchConfig: SearchConfigProps;
  groups: ColumnGroup<TreeNode<TData>, TValue>[];
  rowSelectionConfig: Pick<RowSelectionConfig, 'isEnabled'>;
  sortingConfig?: TableSortConfig;
  defaultVisibleColumns?: string[];
  columnResizeConfig?: ColumnResizeConfig;
  /** Per-column resize caps (column id -> max px) for `maxWidth: 'fit-content'`. */
  fitContentCapsRef?: React.MutableRefObject<Record<string, number>>;
}) {
  const [expanded, setExpanded] = useState<ExpandedState>({});
  const { sortingState, handleSortingChange } = useSortingState(sortingConfig);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const externalColumnSizing = columnResizeConfig?.columnSizing;
  const [columnSizing, setColumnSizing] = useState<ColumnSizingState>(
    externalColumnSizing ?? {}
  );

  // Widths supplied from outside (e.g. read from storage after hydration) win
  // over what's already in state; a drag never changes their identity, so an
  // in-flight resize can't be clobbered by this.
  useEffect(() => {
    if (!externalColumnSizing) return;
    setColumnSizing((old) => ({ ...old, ...externalColumnSizing }));
  }, [externalColumnSizing]);

  // Clamp resized widths to any per-column fit-content cap (set at drag-start).
  const handleColumnSizingChange = (
    updater: ColumnSizingState | ((old: ColumnSizingState) => ColumnSizingState)
  ) => {
    setColumnSizing((old) => {
      const next = typeof updater === 'function' ? updater(old) : updater;
      const caps = fitContentCapsRef?.current;
      if (!caps) return next;
      let clamped: ColumnSizingState | null = null;
      for (const id of Object.keys(next)) {
        const cap = caps[id];
        if (cap != null && next[id] > cap) {
          clamped ??= { ...next };
          clamped[id] = cap;
        }
      }
      return clamped ?? next;
    });
  };
  const [globalFilter, setGlobalFilter] = useState('');
  const {
    pageIndex,
    setPageIndex,
    pageSize,
    setPageSize,
    handlePageSizeChange,
  } = usePaginationState(paginationConfig);

  const flattenedColumns = React.useMemo(() => {
    const columns = groups.map((group) => ({
      id: group.id,
      columns: group.columns.map((col) => createColumnDef(col, group.id)),
    }));

    return columns;
  }, [groups, rowSelectionConfig.isEnabled]);

  const { columnVisibility, setColumnVisibility } = useColumnVisibility(
    groups,
    defaultVisibleColumns
  );

  const table = useReactTable({
    data,
    columns: flattenedColumns,
    getCoreRowModel: getCoreRowModel(),
    ...(columnResizeConfig?.isEnabled
      ? {
          columnResizeMode: 'onChange' as const,
          onColumnSizingChange: handleColumnSizingChange,
        }
      : {}),
    ...(paginationConfig && paginationConfig.isEnabled
      ? {
          getPaginationRowModel: getPaginationRowModel(),
          pageCount: Math.ceil(data.length / pageSize),
          manualPagination: true,
          onPaginationChange: (updater) => {
            const newPagination =
              typeof updater === 'function'
                ? updater({ pageIndex, pageSize })
                : updater;
            setPageIndex(newPagination.pageIndex);
            setPageSize(newPagination.pageSize);
          },
          state: {
            expanded,
            pagination: {
              pageSize,
              pageIndex,
            },
          },
        }
      : {}),
    onColumnFiltersChange: setColumnFilters,
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onSortingChange: handleSortingChange,
    getSortedRowModel: getSortedRowModel(),
    manualSorting: typeof sortingConfig?.onSortingChange === 'function',
    onExpandedChange: (newExpanded) => {
      setExpanded(newExpanded);
    },
    getSubRows: (row: TreeNode<TData>) => {
      if (!row.children) return [];
      return row.children;
    },
    getExpandedRowModel: getExpandedRowModel(),
    state: {
      sorting: sortingState,
      columnFilters,
      columnVisibility,
      globalFilter,
      expanded,
      pagination: paginationConfig.isEnabled
        ? { pageIndex, pageSize }
        : undefined,
      ...(columnResizeConfig?.isEnabled ? { columnSizing } : {}),
    },
    enableSorting: (() => {
      // Derive sorting enabled based on column-level config and presence of sorting handlers/state
      const anyColumnSortable = groups.some((g) =>
        g.columns.some((c) => c?.sort?.shouldSort)
      );
      const hasExternalState = Array.isArray(sortingConfig?.sortingState)
        ? (sortingConfig?.sortingState?.length ?? 0) > 0
        : false;
      const hasOnChange = typeof sortingConfig?.onSortingChange === 'function';
      return anyColumnSortable || hasExternalState || hasOnChange;
    })(),
    globalFilterFn: (row, columnId, filterValue) => {
      const searchableColumns = searchConfig.columnsToEnableSearchFor || [];

      if (!searchableColumns.length) {
        return (
          row.getValue(columnId) !== null &&
          String(row.getValue(columnId))
            .toLowerCase()
            .includes(String(filterValue).toLowerCase())
        );
      }

      return searchableColumns.some((columnKey) => {
        const value = row.getValue(columnKey);
        return (
          value != null &&
          String(value)
            .toLowerCase()
            .includes(String(filterValue).toLowerCase())
        );
      });
    },
  });

  // Calculate the paginated data manually
  const paginatedData = React.useMemo(() => {
    if (!paginationConfig.isEnabled) return table.getRowModel().rows;

    const start = pageIndex * pageSize;
    const end = start + pageSize;
    return table.getRowModel().rows.slice(start, end);
  }, [
    table.getRowModel().rows,
    pageIndex,
    pageSize,
    paginationConfig.isEnabled,
  ]);

  // Handle page size change
  // from hook

  // Extend the return object to include paginated data
  return {
    table: {
      ...table,
      getRowModel: () => ({
        ...table.getRowModel(),
        rows: paginatedData,
      }),
    },
    sorting: sortingState,
    columnFilters,
    globalFilter,
    setGlobalFilter,
    handlePageSizeChange,
    currentPageSize: pageSize,
  };
}
